import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen product image gallery for the storefront.
 *
 *   • Tap thumbnail on catalog / product card → opens here.
 *   • Swipe left / right on mobile OR click the arrow chevrons.
 *   • Tap the image (or the zoom button) to toggle 2× zoom; the zoomed
 *     image pans with drag / touch move so shoppers can inspect details.
 *   • Keyboard: ← / → to navigate, Esc to close.
 *   • Locks background scroll while open.
 *
 * Zero external dependencies — pure CSS transforms + touch handlers.
 */
export default function ProductGallery({ images, initialIndex = 0, name, onClose }) {
  const [idx, setIdx] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const touch = useRef({ startX: 0, startY: 0, lastX: 0, lastY: 0, dx: 0, dy: 0, moved: false });
  const dragging = useRef(false);
  const total = images?.length || 0;

  const go = useCallback(
    (delta) => {
      if (total <= 1) return;
      setZoomed(false);
      setPan({ x: 0, y: 0 });
      setIdx((i) => (i + delta + total) % total);
    },
    [total],
  );

  useEffect(() => {
    // Lock body scroll — critical for mobile so the page under the modal
    // doesn't rubber-band while the user is swiping the gallery.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [go, onClose]);

  // Swipe handlers — same touch events do double duty:
  //   • when zoomed → pan the image
  //   • when not zoomed → swipe between images
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current.startX = t.clientX;
    touch.current.startY = t.clientY;
    touch.current.lastX = t.clientX;
    touch.current.lastY = t.clientY;
    touch.current.dx = 0;
    touch.current.dy = 0;
    touch.current.moved = false;
    dragging.current = true;
  };
  const onTouchMove = (e) => {
    if (!dragging.current) return;
    const t = e.touches[0];
    touch.current.dx = t.clientX - touch.current.startX;
    touch.current.dy = t.clientY - touch.current.startY;
    if (Math.abs(touch.current.dx) > 6 || Math.abs(touch.current.dy) > 6) {
      touch.current.moved = true;
    }
    if (zoomed) {
      // Pan by the incremental delta since the previous move event, so the
      // image follows the finger 1:1 instead of accumulating tiny fractions.
      const incX = t.clientX - touch.current.lastX;
      const incY = t.clientY - touch.current.lastY;
      setPan((p) => ({ x: p.x + incX, y: p.y + incY }));
    }
    touch.current.lastX = t.clientX;
    touch.current.lastY = t.clientY;
  };
  const onTouchEnd = () => {
    dragging.current = false;
    if (!zoomed) {
      const { dx } = touch.current;
      if (dx > 60) go(-1);
      else if (dx < -60) go(1);
    }
  };

  // Click on the image toggles zoom — but only if the user didn't just swipe.
  const onImgClick = () => {
    if (touch.current.moved) return;
    setZoomed((z) => !z);
    setPan({ x: 0, y: 0 });
  };

  if (!total) return null;
  const current = images[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col select-none"
      role="dialog"
      aria-modal="true"
      aria-label={`${name || "Product"} gallery`}
      data-testid="product-gallery"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-12 text-white text-sm">
        <div className="mono text-[11px] tracking-widest opacity-80">
          {idx + 1} / {total}
          {name ? <span className="ml-3 opacity-70 normal-case font-sans">{name}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setZoomed((z) => !z); setPan({ x: 0, y: 0 }); }}
            className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"
            data-testid="pg-zoom"
            aria-label={zoomed ? "Zoom out" : "Zoom in"}
          >
            {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"
            data-testid="pg-close"
            aria-label="Close gallery"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main stage */}
      <div
        className="flex-1 relative overflow-hidden flex items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={current}
          alt={name || "product"}
          onClick={onImgClick}
          className={`max-w-full max-h-full object-contain transition-transform duration-200 ease-out ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
          style={{
            transform: zoomed
              ? `scale(2) translate(${pan.x}px, ${pan.y}px)`
              : "scale(1)",
          }}
          draggable={false}
          data-testid="pg-main-image"
        />

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white items-center justify-center backdrop-blur"
              data-testid="pg-prev"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white items-center justify-center backdrop-blur"
              data-testid="pg-next"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div
          className="px-3 pb-3 pt-2 flex gap-2 overflow-x-auto"
          data-testid="pg-thumbs"
        >
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setZoomed(false); setPan({ x: 0, y: 0 }); setIdx(i); }}
              className={`shrink-0 w-14 h-14 rounded overflow-hidden border-2 ${
                i === idx ? "border-white" : "border-transparent opacity-60 hover:opacity-90"
              }`}
              data-testid={`pg-thumb-${i}`}
              aria-label={`Show image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Reference-image legal disclaimer, same wording as the rest of the shop */}
      <div className="text-center text-[10px] text-white/60 pb-2 mono">
        Image for reference only · actual product may differ
      </div>
    </div>
  );
}
